import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import PageHero from '../components/stitch/PageHero';
import { cardSx } from '../components/stitch/Section';
import faq from '../data/Faq.json';
import { tokens } from '../theme';

// Built to the Stitch "FAQ" screen (project 1587770953174176333): a sticky question list beside
// the full answers, which stay visible (no accordion). The screen's "Advisory Protocol" note is
// left out because it was not site copy.
const number = (index: number) => String(index + 1).padStart(2, '0');

const onNavyOutlineSx = {
  color: '#FFFFFF',
  borderColor: 'rgba(255, 255, 255, 0.5)',
  '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.08)' },
} as const;

function Faq() {
  return (
    <Container sx={{ '& .MuiTypography-root': { overflowWrap: 'anywhere' } }}>
      <PageHero
        eyebrow='Common questions'
        title='Frequently asked questions'
        intro='Answers to the questions teams usually ask before getting in touch: how work starts, pricing, platforms, who does the work, and accessibility.'
      />

      <Box
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 3,
          bgcolor: tokens.surfaceLow,
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '18rem minmax(0, 1fr)' },
          alignItems: 'start',
          gap: { xs: 3, lg: 5 },
        }}
      >
        <Box
          component='nav'
          aria-labelledby='faq-topics'
          sx={{
            ...cardSx,
            p: 3,
            position: { lg: 'sticky' },
            top: { lg: '96px' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              pb: 1.5,
              mb: 1,
              borderBottom: `1px solid ${tokens.outlineVariant}`,
            }}
          >
            <Typography
              id='faq-topics'
              component='h2'
              variant='labelCaps'
              sx={{ color: tokens.accentText }}
            >
              Questions
            </Typography>
            <Typography
              variant='bodySm'
              component='p'
              sx={{ color: tokens.onSurfaceVariant }}
            >
              {faq.items.length} items
            </Typography>
          </Box>
          <Box
            component='ol'
            sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 }}
          >
            {faq.items.map((item, index) => (
              <li key={item.id}>
                <Box
                  component='a'
                  href={`#${item.id}`}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    p: 1.25,
                    borderRadius: 1,
                    color: tokens.onSurfaceVariant,
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    lineHeight: 20 / 14,
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: tokens.surfaceContainer,
                      color: tokens.primary,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <Box
                    component='span'
                    aria-hidden='true'
                    sx={{ color: tokens.accentText, fontWeight: 700, flexShrink: 0 }}
                  >
                    {number(index)}
                  </Box>
                  <Box
                    component='span'
                    sx={{ minWidth: 0 }}
                  >
                    {item.question}
                  </Box>
                </Box>
              </li>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
          {faq.items.map((item, index) => (
            <Box
              component='section'
              key={item.id}
              id={item.id}
              aria-labelledby={`faq-${item.id}`}
              sx={{ ...cardSx, p: { xs: 3, md: 4 }, scrollMarginTop: '96px' }}
            >
              <Typography
                variant='labelCaps'
                component='p'
                aria-hidden='true'
                sx={{
                  display: 'inline-block',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: tokens.surfaceContainer,
                  color: tokens.accentText,
                }}
              >
                Question {number(index)}
              </Typography>
              <Typography
                component='h2'
                id={`faq-${item.id}`}
                variant='h4'
                sx={{ mt: 1.5 }}
              >
                {item.question}
              </Typography>
              <Typography
                variant='body2'
                sx={{ mt: 1.5, maxWidth: '72ch' }}
              >
                {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='faq-next-steps'
        sx={{
          mt: { xs: 6, md: 10 },
          bgcolor: tokens.navy,
          color: '#FFFFFF',
          borderRadius: 3,
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { lg: 'center' },
          justifyContent: 'space-between',
          gap: 3,
          backgroundImage:
            'radial-gradient(30rem 14rem at 85% 50%, rgba(76, 214, 251, 0.10), transparent 70%)',
          '& a:focus-visible, & .Mui-focusVisible': {
            outline: '3px solid #FFFFFF !important',
            outlineOffset: '3px !important',
          },
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            variant='labelCaps'
            component='p'
            sx={{ color: tokens.cyan }}
          >
            Ready to talk
          </Typography>
          <Typography
            component='h2'
            id='faq-next-steps'
            variant='h2'
            sx={{ mt: 1, color: '#FFFFFF' }}
          >
            Next steps
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: { lg: 'flex-end' },
            gap: 1.5,
          }}
        >
          <Button
            component={Link}
            to='/contact-us?subject=cms-workflow-assessment'
            data-track='assessment_request'
            variant='contained'
            endIcon={<ArrowForwardIcon aria-hidden='true' />}
            sx={{ bgcolor: '#FFFFFF', color: '#001026', '&:hover': { bgcolor: '#E2E7FF' } }}
          >
            Request a CMS &amp; workflow assessment
          </Button>
          <Button
            component={Link}
            to='/services#first-engagement'
            variant='outlined'
            sx={onNavyOutlineSx}
          >
            See assessment details on Services
          </Button>
          <Button
            href='/cms-capabilities'
            data-track='capability_statement'
            variant='outlined'
            sx={onNavyOutlineSx}
          >
            View CMS capability statement
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

export default Faq;
